import i18next from 'i18next';

export const EXTERNAL_ORDERS_PATH = '/external/orders';

export const EXTERNAL_ORDER_STATUS = {
  DRAFT: 'draft',
  TO_COME: 'to_come',
  IN_PREPARATION: 'in_preparation',
  PREPARED_COMPLIANT: 'prepared_compliant',
  PREPARED_NON_COMPLIANT: 'prepared_non_compliant',
};

/**
 * Statuses in flow order. The list sorts on the Postgres enum order server-side, which follows that
 * same flow rather than the translated label, and the status filter offers them in that order.
 */
export const EXTERNAL_ORDER_STATUSES_IN_FLOW_ORDER = [
  EXTERNAL_ORDER_STATUS.DRAFT,
  EXTERNAL_ORDER_STATUS.TO_COME,
  EXTERNAL_ORDER_STATUS.IN_PREPARATION,
  EXTERNAL_ORDER_STATUS.PREPARED_COMPLIANT,
  EXTERNAL_ORDER_STATUS.PREPARED_NON_COMPLIANT,
];

export const PREPARED_EXTERNAL_ORDER_STATUSES = [
  EXTERNAL_ORDER_STATUS.PREPARED_COMPLIANT,
  EXTERNAL_ORDER_STATUS.PREPARED_NON_COMPLIANT,
];

/**
 * Logical target of the transition to `Préparée`: the front never sends the two real prepared
 * statuses, the compliant / non compliant sub-state being resolved server-side from the BDL
 * against the BDC.
 */
export const EXTERNAL_ORDER_PREPARED_TARGET_STATUS = 'prepared';

/**
 * Type carried by the lines of an order: `draft` while it is one, `to_prepare` for the ordered
 * quantities (BDC) and `prepared` for the delivered ones (BDL). An order past the draft status
 * carries its BDC lines, and its BDL ones as well from `En préparation` onwards.
 */
export const EXTERNAL_PRODUCT_ORDER_TYPE = {
  DRAFT: 'draft',
  TO_PREPARE: 'to_prepare',
  PREPARED: 'prepared',
};

/**
 * Both prepared statuses read `Préparée`: the compliance is not spelled out in the list, only
 * carried by the color of the badge — green when compliant, orange otherwise.
 */
export const getExternalOrderStatusProperties = () => ({
  [EXTERNAL_ORDER_STATUS.DRAFT]: {
    name: i18next.t('EXTERNAL_ORDERS.LIST.STATUS_DRAFT'),
    color: 'grey',
  },
  [EXTERNAL_ORDER_STATUS.TO_COME]: {
    name: i18next.t('EXTERNAL_ORDERS.LIST.STATUS_TO_COME'),
    color: 'black',
  },
  [EXTERNAL_ORDER_STATUS.IN_PREPARATION]: {
    name: i18next.t('EXTERNAL_ORDERS.LIST.STATUS_IN_PREPARATION'),
    color: 'purple',
  },
  [EXTERNAL_ORDER_STATUS.PREPARED_COMPLIANT]: {
    name: i18next.t('EXTERNAL_ORDERS.LIST.STATUS_PREPARED'),
    color: 'green',
  },
  [EXTERNAL_ORDER_STATUS.PREPARED_NON_COMPLIANT]: {
    name: i18next.t('EXTERNAL_ORDERS.LIST.STATUS_PREPARED'),
    color: 'orange',
  },
});

/**
 * Choices of the `status` filter, one per real status so the compliant and the non compliant
 * prepared orders stay filterable apart. Their badge is identical in the list, so the filter spells
 * the compliance out — that label is the only place where it is written down.
 */
export const getExternalOrderStatusFilterChoices = () => {
  const statusProperties = getExternalOrderStatusProperties();

  const filterNames = {
    [EXTERNAL_ORDER_STATUS.PREPARED_COMPLIANT]: i18next.t(
      'EXTERNAL_ORDERS.LIST.FILTER_STATUS_PREPARED_COMPLIANT',
    ),
    [EXTERNAL_ORDER_STATUS.PREPARED_NON_COMPLIANT]: i18next.t(
      'EXTERNAL_ORDERS.LIST.FILTER_STATUS_PREPARED_NON_COMPLIANT',
    ),
  };

  return EXTERNAL_ORDER_STATUSES_IN_FLOW_ORDER.map((status) => ({
    value: status,
    color: statusProperties[status].color,
    name: filterNames[status] || statusProperties[status].name,
  }));
};

export const EXTERNAL_ORDER_TAB = {
  ALL: 'all',
  TO_COME: 'to-come',
  IN_PREPARATION: 'in-preparation',
  PREPARED: 'prepared',
};

/**
 * The tabs are not a dedicated request parameter: each of them narrows the `status` condition sent
 * to the list route. `Toutes` carries no status of its own, so it also shows the drafts.
 */
export const getExternalOrderTabs = () => [
  {
    key: EXTERNAL_ORDER_TAB.ALL,
    name: i18next.t('EXTERNAL_ORDERS.LIST.TAB_ALL'),
    statuses: null,
  },
  {
    key: EXTERNAL_ORDER_TAB.TO_COME,
    name: i18next.t('EXTERNAL_ORDERS.LIST.TAB_TO_COME'),
    statuses: [EXTERNAL_ORDER_STATUS.TO_COME],
  },
  {
    key: EXTERNAL_ORDER_TAB.IN_PREPARATION,
    name: i18next.t('EXTERNAL_ORDERS.LIST.TAB_IN_PREPARATION'),
    statuses: [EXTERNAL_ORDER_STATUS.IN_PREPARATION],
  },
  {
    key: EXTERNAL_ORDER_TAB.PREPARED,
    name: i18next.t('EXTERNAL_ORDERS.LIST.TAB_PREPARED'),
    statuses: PREPARED_EXTERNAL_ORDER_STATUSES,
  },
];

export const DEFAULT_ORDER_BY = 'deliveryDate';
export const DEFAULT_ORDER_TYPE = 'desc';
export const DEFAULT_MAX_PER_PAGE = 20;
export const FIRST_PAGE = 1;

/**
 * Query param holding the active tab. It has no counterpart in the list route — the tab is turned
 * into a `status` condition — but it belongs in the URL so a page stays shareable with its tab.
 */
export const TAB_QUERY_PARAM = 'tab';

/**
 * Router state carrying the prefilled payload of a duplication (COR-1691) to the creation page.
 * Nothing exists server-side at that point: the duplicated order is only created when the form is
 * saved or validated.
 */
export const DUPLICATED_ORDER_STATE_KEY = 'duplicatedExternalOrder';

/**
 * The options of the client filter come from the paginated external clients route, which has no
 * unpaginated variant yet: a single page large enough to hold the whole perimeter stands in for it.
 */
export const EXTERNAL_CLIENTS_MAX_PER_PAGE = 1000;

/**
 * Steps of the order flow shown in the form header. Both prepared statuses land on the same
 * `Préparée` step — as in the list, the compliance is not spelled out.
 */
export const getExternalOrderFormSteps = () => [
  {
    key: EXTERNAL_ORDER_STATUS.DRAFT,
    label: i18next.t('EXTERNAL_ORDERS.LIST.STATUS_DRAFT'),
    statuses: [EXTERNAL_ORDER_STATUS.DRAFT],
  },
  {
    key: EXTERNAL_ORDER_STATUS.TO_COME,
    label: i18next.t('EXTERNAL_ORDERS.LIST.STATUS_TO_COME'),
    statuses: [EXTERNAL_ORDER_STATUS.TO_COME],
  },
  {
    key: EXTERNAL_ORDER_STATUS.IN_PREPARATION,
    label: i18next.t('EXTERNAL_ORDERS.LIST.STATUS_IN_PREPARATION'),
    statuses: [EXTERNAL_ORDER_STATUS.IN_PREPARATION],
  },
  {
    key: EXTERNAL_ORDER_STATUS.PREPARED_COMPLIANT,
    label: i18next.t('EXTERNAL_ORDERS.LIST.STATUS_PREPARED'),
    statuses: PREPARED_EXTERNAL_ORDER_STATUSES,
  },
];

/** A VAT rate is usually whole (10 %) but can carry decimals (5,5 %). */
export const EXTERNAL_ORDER_VAT_RATE_DECIMALS = 2;

/** Quantities are whole products: the form neither accepts nor displays decimals. */
export const EXTERNAL_ORDER_QUANTITY_LIMITS = { min: 0, max: 999999 };
